import * as Yup from 'yup';

/**
 * Co-insurer split validation (FPX-5864). The lead (the layer's own carrier) is
 * implicit and holds the remainder; `coInsurers` lists only the *other*
 * participants. A split is valid when it is empty (no split — the lead holds
 * 100%) or every co-insurer has a carrier and a positive share and, together,
 * they leave the lead carrier a positive remainder (Σ < 100%).
 */
export const CO_INSURER_SPLIT_TOTAL = 100;

type CoInsurerShare = { carrier?: string | null; percentage?: number | null };

export const sumCoInsurerPercentages = (
  rows: ReadonlyArray<CoInsurerShare>
) => rows.reduce((total, { percentage }) => total + (percentage ?? 0), 0);

/** The implicit share retained by the lead carrier: 100 − Σ(co-insurer shares). */
export const leadCarrierRemainder = (
  rows: ReadonlyArray<CoInsurerShare>
): number => CO_INSURER_SPLIT_TOTAL - sumCoInsurerPercentages(rows);

/** A co-insurer row is complete when it names a carrier and a positive share. */
export const isCoInsurerRowComplete = (row: CoInsurerShare): boolean =>
  Boolean(row.carrier) && row.percentage != null && row.percentage > 0;

const everyRowComplete = (rows: ReadonlyArray<CoInsurerShare>): boolean =>
  rows.every(isCoInsurerRowComplete);

export const isCoInsurerSplitValid = (
  rows: ReadonlyArray<CoInsurerShare>
): boolean =>
  rows.length === 0 ||
  (everyRowComplete(rows) && leadCarrierRemainder(rows) > 0);

export const coInsurersSchema = Yup.array()
  .of(
    Yup.object({
      carrier: Yup.string().required(),
      percentage: Yup.number().nullable().required(),
    })
  )
  .test(
    'lead-keeps-remainder',
    'Co-insurer shares must leave the primary carrier above 0%.',
    (rows) => !rows?.length || isCoInsurerSplitValid(rows)
  );
